import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import ContentCopyIcon from "@mui/icons-material/ContentCopy"
import { useState } from "react"
import { QRCodeSVG } from "qrcode.react"
import Hosts from "../../../../core/functions/HostFunctions"
import React from "react"
import { Trans, useTranslation } from "react-i18next"

export const isPixPayload = (value?: string) => {
  return (value || "").trim().startsWith("000201")
}

export const isBase64Png = (value?: string) => {
  const normalizedValue = (value || "").trim()
  return (
    normalizedValue.startsWith("iVBORw0KGgo") ||
    normalizedValue.startsWith("data:image/png;base64,")
  )
}

const normalizeBase64Png = (value: string) => {
  return value
    .trim()
    .replace("data:image/png;base64,", "")
    .replace(/\s/g, "+")
}

export const toImageSrc = (value: string) => {
  return `data:image/png;base64,${normalizeBase64Png(value)}`
}

export const isImageUrl = (value?: string) => {
  return (value || "").trim().startsWith("http")
}

export const shouldRenderAsImage = (value?: string) => {
  return isImageUrl(value) || isBase64Png(value)
}

export const PixSuccess = ({
  qrCode,
  qrCodeUrl,
}: {
  qrCode: string
  qrCodeUrl: string
}) => {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  const qrCodeImageValue = [qrCodeUrl, qrCode].find(shouldRenderAsImage) || ""
  const qrCodeImage = isImageUrl(qrCodeImageValue)
    ? qrCodeImageValue.trim()
    : isBase64Png(qrCodeImageValue)
    ? toImageSrc(qrCodeImageValue)
    : ""
  const copyCode = [qrCode, qrCodeUrl].find(isPixPayload) || qrCode
  const qrCodeValue = isPixPayload(copyCode) ? copyCode : qrCodeUrl || qrCode

  const handleClick = async () => {
    await Hosts.copyTextToClipboard(copyCode)
    setCopied(true)
  }

  return (
    <Stack
      direction={"column"}
      spacing={2}
      alignItems="center"
      justifyContent="center"
      sx={{ pt: 2 }}
    >
      <Stack
        direction="column"
        spacing={2}
        alignItems="center"
        justifyContent="center"
        width="100%"
      >
        <Typography
          color="#38393B"
          fontSize="18px"
          lineHeight="24px"
          fontWeight={700}
        >
          {t("postPurchase.pix.scan")}
        </Typography>
        {qrCodeImage ? (
          <img src={qrCodeImage} alt="qrCode" />
        ) : (
          <QRCodeSVG value={qrCodeValue} size={200} />
        )}

        <Stack direction="row" spacing={2} width="100%" alignItems="center">
          <Box width="100%" height="1px" sx={{ backgroundColor: "divider" }} />
          <Typography>{t("postPurchase.pix.or")}</Typography>
          <Box width="100%" height="1px" sx={{ backgroundColor: "divider" }} />
        </Stack>

        <Typography
          color="#38393B"
          fontSize="18px"
          lineHeight="24px"
          fontWeight={700}
        >
          {t("postPurchase.pix.copyPaste")}
        </Typography>

        <TextField
          fullWidth
          aria-readonly
          size="small"
          value={copyCode}
          maxRows={1}
          inputProps={{
            "data-testid": "paymentQrCode",
          }}
          sx={{ maxWidth: "sm" }}
        />
        <Button
          variant="contained"
          size="large"
          onClick={handleClick}
          startIcon={<ContentCopyIcon />}
          data-testid="buttonCopyQrCode"
          sx={{ textTransform: "none" }}
        >
          {t("postPurchase.pix.copyCode")}
        </Button>
      </Stack>
      <Container maxWidth="sm" disableGutters>
        <Box sx={{ pt: 3 }}>
          {/*<Typography variant="body2">1. <strong>Aponte seu celular para essa tela</strong> para capturar o código</Typography>
        <Typography variant="body2">2. Abra seu <strong>aplicativo de pagamentos</strong></Typography>
        <Typography variant="body2">3. Confirme os dados e <strong>finalize o pagamento pelo aplicativo</strong></Typography>
        <Typography variant="body2">4. Será enviada uma <strong>confirmação de compra</strong> para você</Typography>*/}
          <Typography
            color="#343948"
            fontSize="16px"
            lineHeight="20px"
            fontWeight={700}
          >
            {t("postPurchase.pix.chooseOption")}
          </Typography>
          <ol type="1">
            <li>
              <Typography
                gutterBottom
                color="#343948"
                fontSize="14px"
                lineHeight="18px"
              >
                <Trans
                  i18nKey="postPurchase.pix.stepScan"
                  components={{ b: <strong style={{ fontWeight: 600 }} /> }}
                />
              </Typography>
            </li>
            <li>
              <Typography
                gutterBottom
                color="#343948"
                fontSize="14px"
                lineHeight="18px"
              >
                <Trans
                  i18nKey="postPurchase.pix.stepCopy"
                  components={{ b: <strong style={{ fontWeight: 600 }} /> }}
                />
              </Typography>
            </li>
            <li>
              <Typography
                gutterBottom
                color="#343948"
                fontSize="14px"
                lineHeight="18px"
              >
                {t("postPurchase.pix.stepConfirm")}
              </Typography>
            </li>
            <li>
              <Typography
                gutterBottom
                color="#343948"
                fontSize="14px"
                lineHeight="18px"
              >
                {t("postPurchase.pix.stepEmail")}
              </Typography>
            </li>
          </ol>
        </Box>
      </Container>

      {/*<Alert sx={{ my: 1 }} severity="info">
        Você também pode finalizar o pagamento copiando e colando o código em
        seu aplicativo.
      </Alert>*/}

      <Snackbar
        open={copied}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        message={t("postPurchase.order.codeCopied")}
        autoHideDuration={2000}
        onClose={() => setCopied(false)}
      >
        <Alert
          variant="filled"
          onClose={() => setCopied(false)}
          severity="info"
          sx={{ width: "100%" }}
        >
          {t("postPurchase.order.codeCopied")}
        </Alert>
      </Snackbar>
    </Stack>
  )
}
